import {
  DateTimeFormatOptions,
  formatFileSize,
  formatIso8601String,
  formatNumber,
  formatPhoneNumber,
  formatUnixTime,
  NumericFormatOptions,
} from "@interface48/formatting";
import Link from "@material-ui/core/Link";
import { createStyles, Theme, withStyles, WithStyles } from "@material-ui/core/styles";
import React from "react";
import { JsonSchema } from "../models";
import { HtmlContent } from "../shared";
import { WidgetProps } from "./models";

const styles = (theme: Theme) =>
  createStyles({
    root: {
      marginTop: theme.spacing(2),
      padding: "7px 0 9px",
      // 	Sequences of whitespace will collapse into a single whitespace. Text will wrap when necessary,
      // and on line breaks
      whiteSpace: "pre-line",
      color: theme.palette.text.primary,
    },
  });

interface ReadOnlyWidgetProps
  extends WidgetProps<string | number | boolean | null | undefined>,
    WithStyles<typeof styles> {
  options: {
    format?: {
      type: "date" | "time" | "date-time" | "numeric" | "phone-number" | "file-size" | "email" | "uri" | "html";
      options?: DateTimeFormatOptions | NumericFormatOptions;
    };
    emptyValue?: string;
  };
}

export const ReadOnlyWidgetComponent = (props: ReadOnlyWidgetProps) => {
  const { id, value, schema, options, classes } = props;
  const { format: schemaFormat, type } = schema;

  const format = schemaFormat ?? options.format?.type;
  const emptyValue = options && options.emptyValue;

  let displayValue: React.ReactNode | undefined;
  let displayTitle: string | undefined;

  // If this is a value corresponding to a key/value pair that exists within a oneOf or anyOf array, or if this is an
  // array value corresponding to one or more key/value pairs that exist within a oneOf or anyOf array,
  // resolve the display value(s)...
  if (
    schema &&
    (schema.oneOf || schema.anyOf || (schema.items && ((schema.items as any).oneOf || (schema.items as any).anyOf)))
  ) {
    const optionItems = (schema.items
      ? (schema.items as any).oneOf || (schema.items as any).anyOf
      : schema.oneOf || schema.anyOf) as JsonSchema[];

    if (!optionItems) {
      throw Error(`${schema.oneOf ? "oneOf" : "anyOf"} array is not defined for ${id}.`);
    }

    const values = (value != null ? (Array.isArray(value) ? value : [value]) : null) as Array<string | number> | null;

    const displayValues = values != null ? values.map(value => optionItems.find(o => o.const === value)!.title!) : [];

    displayValue = displayValues.length ? displayValues.join(", ") : emptyValue || "(None)";
  }
  // Otherwise, if this is a boolean-type value, default to a Yes/No display value...
  else if (type === "boolean") {
    displayValue = value != null ? (value ? "Yes" : "No") : emptyValue || "";
  }
  // Otherwise, if there is a particular formatter to apply against this value...
  else if (format) {
    switch (format) {
      case "date":
      case "time":
      case "date-time":
        // If date format, then include only the Date portion, otherwise, include all portions
        const dateTimeIncludePortions = format === "date" ? "date" : format === "time" ? "time" : undefined;
        const dateTimeFormatOptions = options.format?.options as DateTimeFormatOptions | undefined;
        // If the DateTime value has been provided as a number, then assume it is Unix time...
        if (value && schema.type === "number") {
          displayValue = formatUnixTime(value as number, dateTimeIncludePortions, dateTimeFormatOptions);
          // If the value is being displayed as Relative Time, then display the Absolute Time within the title...
          if (dateTimeFormatOptions && dateTimeFormatOptions.reference === "relative") {
            displayTitle = formatUnixTime(value as number, dateTimeIncludePortions, {
              ...dateTimeFormatOptions,
              reference: "local",
            });
          }
        }
        // Otherwise, assume the value must be in ISO 8601 string format...
        else if (value && schema.type === "string") {
          displayValue = formatIso8601String(value as string, dateTimeIncludePortions, dateTimeFormatOptions);
          // If the value is being displayed as Relative Time, then display the Absolute Time within the title...
          if (dateTimeFormatOptions && dateTimeFormatOptions.reference === "relative") {
            displayTitle = formatIso8601String(value as string, dateTimeIncludePortions, {
              ...dateTimeFormatOptions,
              reference: "local",
            });
          }
        } else {
          displayValue = emptyValue || "";
        }
        break;
      case "numeric":
        const numericFormatOptions = options.format?.options as NumericFormatOptions | undefined;
        displayValue = value != null ? formatNumber(value as number, numericFormatOptions) : emptyValue || "";
        break;
      case "phone-number":
        displayValue = value != null ? formatPhoneNumber(`${value}`) : emptyValue || "";
        break;
      case "file-size":
        displayValue = value != null ? formatFileSize(value as number) : emptyValue || "";
        break;
      case "email":
        displayValue = value != null ? <Link href={`mailto:${value}`}>{value}</Link> : emptyValue || "";
        break;
      case "uri":
        displayValue =
          value != null ? (
            <Link href={`${value}`} target="_blank" rel="noopener">
              {" "}
              {value}{" "}
            </Link>
          ) : (
            emptyValue || ""
          );
        break;
      case "html":
        displayValue = (
          <HtmlContent component={"div"}>{value != null ? (value as string) : emptyValue || ""}</HtmlContent>
        );
        break;
      default:
        throw Error(`Unsupported ReadOnlyWidget formatter of ${format} specified for ${id}.`);
    }
  } else {
    displayValue = value != null ? `${value}` : emptyValue || "";
  }

  return (
    <div className={classes.root} style={displayTitle ? { cursor: "help" } : undefined} title={displayTitle}>
      {displayValue}
    </div>
  );
};

export const ReadOnlyWidget = withStyles(styles)(ReadOnlyWidgetComponent);
